import { useId, useState, type ReactNode } from 'react';
import { EyeIcon, EyeOffIcon } from './icons';

/**
 * A password box with an eye button (PR B1): show / hide what was typed.
 * The label is a real <label htmlFor> outside any button (screen readers and
 * password managers read the right name).
 */
export function PasswordField(props: {
  label: string;
  name: string;
  autoComplete: 'current-password' | 'new-password';
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  required?: boolean;
  id?: string;
  /** shown on the label row, right side (e.g. "Forgot password?") */
  labelExtra?: ReactNode;
  /** under the box (e.g. the strength meter) */
  children?: ReactNode;
}) {
  const autoId = useId();
  const id = props.id ?? `pw-${autoId}`;
  const [shown, setShown] = useState(false);
  return (
    <div className="field">
      <span className="field-row">
        <label htmlFor={id}>{props.label}</label>
        {props.labelExtra}
      </span>
      <span className="password-box">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          name={props.name}
          autoComplete={props.autoComplete}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          required={props.required}
          autoFocus={props.autoFocus}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
        />
        <button
          type="button"
          className="password-eye"
          aria-label={shown ? `Hide ${props.label.toLowerCase()}` : `Show ${props.label.toLowerCase()}`}
          aria-pressed={shown}
          aria-controls={id}
          title={shown ? 'Hide' : 'Show'}
          onClick={() => setShown((v) => !v)}
        >
          {shown ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
        </button>
      </span>
      {props.children}
    </div>
  );
}
